<template>
  <b-row>
    <b-col md="3">
      <div class="info" v-if="reference.customerName && isGeneralUser">
        <div class="d-flex">
          <div>
            <div class="category">{{$t("attributes.customer")}}</div>
            {{ reference.customerName}}
          </div>
          <div>
            <img :src="iconImage" class="ml-4 mt-1" style="max-height:30px; max-width:60px" />
          </div>
        </div>
      </div>

      <div class="info" v-if="reference.referenceNumber">
        <div class="category">{{$t("attributes.referenceNumber")}}</div>
        {{ reference.referenceNumber}}
      </div>

      <div class="info">
        <div class="category">
          {{$t("attributes.location")}}
          <b-link @click="openMap()">{{$t("mapView")}}</b-link>
        </div>
        {{ reference.location.city}}, {{reference.location.countryCode.toUpperCase()}}
      </div>

      <div class="info" v-if="localize(reference.industry)">
        <div class="category">{{$t("attributes.industry")}}</div>
        {{ reference.industry | localize}}
      </div>

      <div class="info" v-if="localize(reference.clientApplication)">
        <div class="category">{{$t("attributes.clientApplication")}}</div>
        {{ reference.clientApplication | localize}}
      </div>

      <div class="info" v-if="reference.year && isGeneralUser">
        <div class="category">{{$t("attributes.year")}}</div>
        {{ reference.year}}
      </div>

      <div class="info" v-if="reference.productFamily">
        <div class="category">{{$t("attributes.productFamily")}}</div>
        {{ reference.productFamily.join(", ")}}
      </div>

      <div
        class="info"
        v-if="localize(reference.productScope)"
      >
        <div class="category">{{$t("attributes.productScope")}}</div>
        {{ reference.productScope[currentLanguage]}}
      </div>

      <div class="info" v-if="reference.projectNumber && isGeneralUser">
        <div class="category">{{$t("attributes.projectNumber")}}</div>
        {{ reference.projectNumber}}
      </div>

      <div class="info" v-if="reference.integrator && isGeneralUser">
        <div class="category">{{$t("attributes.integrator")}}</div>
        {{ reference.integrator}}
      </div>

      <div class="info" v-if="reference.projectVolume && isGeneralUser">
        <div class="category">{{$t("attributes.projectVolume")}}</div>
        {{ reference.projectVolume.value }} {{ reference.projectVolume.currency }}
      </div>

      <div class="info" v-if="reference.eeposContact">
        <div class="category">{{$t("attributes.eeposContact")}}</div>
        {{ reference.eeposContact}}
      </div>
    </b-col>
    <b-col :md="galleryImages.length>0?6:9">
      <div class="reference-content" v-html="localize(reference.description)" />
      <div
        class="reference-content"
        v-if="localize(reference.additionalInformation)"
      >
        <h3 v-html="$t('attributes.additionalInformation')" />
        <div v-html="localize(reference.additionalInformation)" />
      </div>
      <div
        class="reference-content"
        v-if="localize(reference.advantages) && isInternalUser"
      >
        <h3 v-html="$t('attributes.advantages')" />
        <div v-html="localize(reference.advantages)" />
      </div>
      <div class="reference-content" v-if="localize(reference.customerStatement)">
        <h3 v-html="$t('attributes.customerStatement')" />
        <div v-html="localize(reference.customerStatement)" />
      </div>
    </b-col>
    <b-col v-if="galleryImages.length>0" :md="galleryImages.length>0?3:3">
      <div
        v-for="(img, index) in galleryImages"
        :key="img"
        class="gallery-images"
        style="width:100%"
      >
        <img
          :src="img"
          @click="openSlider(index)"
          class="d-block img-fluid mb-2"
          style="width: 100%;  object-fit: contain ;"
        />
      </div>
      <b-modal ref="galleryModal" size="lg" hide-footer>
        <Slider :images="galleryImages" v-model="activeSlide" />
      </b-modal>
    </b-col>

    <b-row>
      <b-col
        sm="12"
        md="6"
        lg="4"
        class="mt-3"
        v-if="
          reference.advantages != null && reference.advantages.length > 0
        "
      >
        <h4>{{ $t("attributes.advantages") }}</h4>
        <p>{{ reference.advantages }}</p>
      </b-col>

      <b-col
        sm="12"
        md="6"
        lg="4"
        class="mt-3"
        v-if="
          reference.additionalInformation != null && reference.additionalInformation.length > 0
        "
      >
        <h4>{{ $t("attributes.additionalInformation") }}</h4>
        <p>{{ reference.additionalInformation }}</p>
      </b-col>

      <b-col
        sm="12"
        md="6"
        lg="4"
        class="mt-3"
        v-if="
          reference.customerStatement != null && reference.customerStatement.length > 0
        "
      >
        <h4>{{ $t("attributes.customerStatement") }}</h4>
        <p>{{ reference.customerStatement }}</p>
      </b-col>
    </b-row>

    <b-modal ref="mapModal" size="xl" hide-footer @shown="modalShown">
      <div style="height: 400px; width: 100%;">
        <l-map
          ref="mymap"
          :options="{
          zoomControl: true,
          dragging: true,
          scrollWheelZoom: true,
        }"
          :zoom="map.zoom"
          :center="convert(reference.location.coordinates.lat, reference.location.coordinates.lon)"
          :min-zoom="1"
          :max-zoom="10"
          style="height: 100%;"
          @update:center="centerUpdate"
          @update:zoom="zoomUpdate"
        >
          <l-tile-layer :url="$t('map.tiles')" :attribution="map.attribution" />
          <l-marker
            :lat-lng="convert(reference.location.coordinates.lat, reference.location.coordinates.lon)"
          ></l-marker>
        </l-map>
      </div>
    </b-modal>
  </b-row>
</template>

<script>
import { latLng } from "leaflet";
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";
import Slider from "./Slider";
import currentUserMixin from "../current-user.js";
import localize from "@/filters/localize.js";

export default {
  name: "ProjectInformation",
  props: {
    reference: Object,
  },
  mixins: [currentUserMixin],
  components: {
    Slider,
    LMap,
    LTileLayer,
    LMarker,
  },

  data: () => ({
    map: {
      zoom: 3,
      center: latLng(33.9217095, -118.33509920546506),
      attribution:
        '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      withPopup: latLng(47.41322, -1.219482),
      withTooltip: latLng(47.41422, -1.250482),
    },
    activeSlide: 0,
  }),
  computed: {
    currentLanguage() {
      return this.$i18n.locale;
    },
    galleryImages() {
      return this.reference.attachedFiles
        .filter((file) => file.use.includes("GALLERY"))
        .map(
          (file) =>
            `${process.env.VUE_APP_API_BASE_URL}/files/${file.id}?img_w=1920`
        );
    },
    iconImage() {
      return this.reference.attachedFiles
        .filter((file) => file.use.includes("ICON"))
        .map(
          (file) =>
            `${process.env.VUE_APP_API_BASE_URL}/files/${file.id}?img_w=70`
        )[0];
    },
  },
  methods: {
    localize,
    modalShown() {
      setTimeout(() => {
        //mapObject is a property that is part of leaflet
        this.$refs.mymap.mapObject.invalidateSize();
      }, 100);
    },
    openMap() {
      this.$refs["mapModal"].show();
    },
    openSlider(index) {
      this.activeSlide = index;
      this.$refs["galleryModal"].show();
    },
    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    },
    centerUpdate(center) {
      this.currentCenter = center;
    },
    convert(lat, lng) {
      return latLng(lat, lng);
    },
  }
};
</script>

<style lang="scss">
@import "../variables.scss";
.reference-content h3 {
  color: $eeposblue;
  font-weight: bold;
  font-size: 16px;
  margin: 20px 0px 5px 0px;
}
.gallery-images img {
  width: 100%;
  cursor: pointer;
}
.info {
  margin-bottom: 15px;
  a {
    text-transform: none;
    font-weight: 400;
  }
  .category {
    color: rgb(104, 103, 103);
    font-weight: bold;
    text-transform: uppercase;
  }
}

.modal-header {
  border-bottom: none;
}
.modal-body {
  padding: 10px 0 40px 0;
  .carousel {
    margin-top: 0px !important;
  }
}
.modal-content{
  border: none
}
</style>